import { useEffect, useMemo, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ReceiveModal from "@/components/ReceiveModal";
import QuickReceiveModal from "@/components/QuickReceiveModal";
import PrintLabelsModal from "@/components/PrintLabelsModal";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PackageIcon, PlusIcon, ArrowRightIcon, ClipboardTextIcon, FileTextIcon, LightningIcon,
  BarcodeIcon,
} from "@phosphor-icons/react";

import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
/**
 * /inventario/ingresos
 * Módulo para registrar el ingreso de productos comprados al inventario.
 *
 * Dos vías:
 *   A) Contra Orden de Compra existente  →  usa POST /purchase-orders/{oid}/receive
 *      (misma lógica que en /compras, actualiza inventario + costo ponderado + PEPS
 *       y opcionalmente genera factura de compra automáticamente)
 *   B) Ingreso directo (sin OC)  →  redirige a /compras/factura/nueva
 *      (crea factura de compra directa que también genera las entradas de inventario)
 */
export default function InventoryReceive() {
  const [pending, setPending] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiving, setReceiving] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [labelsRef, setLabelsRef] = useState(null);  // referencia (OC) para modal etiquetas
  const [q, setQ] = usePageState("q", "");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: pos }, { data: recs }] = await Promise.all([
        api.get("/purchase-orders"),
        // Historial real de recepciones agrupado por referencia (OC)
        api.get("/goods-receipts").catch(() => ({ data: [] })),
      ]);
      // OCs pendientes de recibir (aprobada o parcialmente recibida)
      const openPOs = (pos || []).filter((p) => p.estado === "aprobada" || p.estado === "recibida_parcial");
      setPending(openPOs);
      setReceipts(recs || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const busqueda = useLiveSearch(pending, (p) => [p.numero, p.proveedor_nombre, p.proveedor_codigo], { estado: [q, setQ] });
  const filtered = busqueda.resultados;

  return (
    <Layout title="Ingresos al inventario" subtitle="Recepción de mercancía comprada — entra al PEPS con lote, fecha y bodega">
      {/* Tres accesos principales — Ingreso rápido es el más destacado */}
      <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="receive-hub">
        <button
          onClick={() => setQuickOpen(true)}
          className="relative overflow-hidden bg-gradient-to-br from-agro-green/15 to-agro-green/5 border border-agro-green/40 rounded-xl p-6 text-left group hover:border-agro-green/70 transition"
          data-testid="option-quick"
        >
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-agro-green/20 group-hover:bg-agro-green/30 transition" />
          <div className="relative">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-agro-green mb-3">
              <LightningIcon size={12} weight="fill" /> Recomendado
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Ingreso rápido</h3>
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Producto que ya compraste nuevamente. Captura batch, lote, cantidad, precio de compra, fechas y bodega — y actualiza el precio de venta desde el mismo formulario.
            </p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-agro-green group-hover:text-agro-green-hover">
              Registrar ingreso <ArrowRightIcon size={14} weight="bold" />
            </span>
          </div>
        </button>

        <div className="relative overflow-hidden bg-card border border-agro-yellow/30 rounded-xl p-6 group hover:border-agro-yellow/60 transition"
             data-testid="option-con-oc">
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-agro-yellow/10 group-hover:bg-agro-yellow/20 transition" />
          <div className="relative">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-agro-yellow mb-3">
              <ClipboardTextIcon size={12} weight="fill" /> Con orden de compra
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Recibir contra OC</h3>
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Recibe múltiples ítems de una orden de compra previa. Ideal cuando ya generaste la OC y el proveedor entrega.
            </p>
            <div className="text-xs text-text-muted mb-4">
              {loading ? "Cargando…" : (
                <span><b className="text-agro-yellow">{pending.length}</b> OC{pending.length === 1 ? "" : "s"} pendiente{pending.length === 1 ? "" : "s"} de recibir</span>
              )}
            </div>
            <a href="#pending-list" className="inline-flex items-center gap-2 text-sm font-semibold text-agro-yellow hover:text-agro-yellow/80" data-testid="btn-see-pending">
              Ver lista abajo <ArrowRightIcon size={14} />
            </a>
          </div>
        </div>

        <button
          onClick={() => navigate("/compras/factura/nueva")}
          className="relative overflow-hidden bg-card border border-border rounded-xl p-6 text-left group hover:border-text-secondary/60 transition"
          data-testid="option-sin-oc"
        >
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white/[0.02] group-hover:bg-white/[0.05] transition" />
          <div className="relative">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-text-secondary mb-3">
              <PlusIcon size={12} weight="bold" /> Factura completa
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Factura de compra directa</h3>
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Múltiples productos en una sola factura con proveedor, impuestos, cuentas por pagar y contabilidad automática.
            </p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-text-secondary group-hover:text-text-primary">
              Nueva factura <ArrowRightIcon size={14} weight="bold" />
            </span>
          </div>
        </button>
      </div>

      {/* Lista de OCs pendientes de recibir */}
      <div id="pending-list" className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-semibold flex items-center gap-2">
              <PackageIcon size={16} weight="duotone" className="text-agro-green" /> Órdenes de compra pendientes de recibir
            </h3>
            <p className="text-xs text-text-muted mt-1">Selecciona una para iniciar la recepción de mercancía.</p>
          </div>
          <SearchBox busqueda={busqueda} placeholder="Buscar por N° o proveedor…" testid="search-pending-po" label="Buscar órdenes de compra pendientes" className="max-w-md" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">N°</th>
                <th className="p-3">Proveedor</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Estado</th>
                <th className="p-3 text-right">Ítems</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 pr-5 text-right">Acción</th>
              </tr>
            </thead>
            <tbody data-testid="pending-po-tbody">
              {loading ? <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : filtered.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-text-muted">
                  {busqueda.activa ? `Sin resultados para «${busqueda.texto}».` : "Sin OCs pendientes de recibir"}
                </td></tr>
              ) : filtered.map((p) => (
                <tr key={p.id} className="border-t border-border/40 hover:bg-white/[0.02] transition" data-testid={`pending-po-${p.numero}`}>
                  <td className="p-3 pl-5 font-mono text-agro-green text-sm">{p.numero}</td>
                  <td className="p-3 text-sm">{p.proveedor_nombre}</td>
                  <td className="p-3 text-xs text-text-secondary">{fmtDate(p.fecha)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${p.estado === "recibida_parcial" ? "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30" : "bg-agro-green/10 text-agro-green border-agro-green/30"}`}>
                      {p.estado === "recibida_parcial" ? "Parcial" : "Aprobada"}
                    </span>
                  </td>
                  <td className="p-3 text-right text-xs">{(p.items || []).length}</td>
                  <td className="p-3 text-right text-sm">{fmtL(p.total)}</td>
                  <td className="p-3 pr-5 text-right">
                    <button
                      onClick={() => setReceiving(p)}
                      className="inline-flex items-center gap-1.5 bg-agro-yellow/10 hover:bg-agro-yellow/20 text-agro-yellow font-semibold text-xs rounded-md px-3 py-1.5 transition"
                      data-testid={`btn-recibir-${p.numero}`}
                    >
                      <PackageIcon size={12} weight="duotone" /> Recibir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Últimas recepciones registradas — para referencia rápida */}
      {receipts.length > 0 && (
        <div className="mt-6 bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="recent-receipts">
          <div className="p-4 border-b border-border/60 flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold flex items-center gap-2">
              <FileTextIcon size={16} weight="duotone" className="text-text-secondary" /> Últimas recepciones registradas
            </h3>
            <Link to="/compras" className="text-xs text-text-muted hover:text-agro-green">Ver todas →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-3 pl-5">Fecha</th>
                  <th className="p-3">Referencia (OC)</th>
                  <th className="p-3">Bodegas</th>
                  <th className="p-3 text-right">Líneas</th>
                  <th className="p-3 text-right">Total unidades</th>
                  <th className="p-3 text-right">Total costo</th>
                  <th className="p-3 pr-5 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {receipts.slice(0, 10).map((r, i) => (
                  <tr key={r.referencia || i} className="border-t border-border/40 hover:bg-sidebar/40 transition-colors" data-testid={`receipt-row-${i}`}>
                    <td className="p-3 pl-5 text-xs text-text-secondary font-mono">{fmtDate(r.fecha)}</td>
                    <td className="p-3 text-sm font-mono text-agro-green">{r.referencia || "—"}</td>
                    <td className="p-3 text-xs text-text-secondary">{(r.bodegas || []).join(", ") || "—"}</td>
                    <td className="p-3 text-right text-xs text-text-muted">{r.total_lineas || 0}</td>
                    <td className="p-3 text-right text-xs text-agro-yellow font-mono">{r.total_unidades || 0}</td>
                    <td className="p-3 text-right text-xs text-text-secondary font-mono">{fmtL(r.total_costo || 0)}</td>
                    <td className="p-3 pr-5 text-center">
                      <button
                        type="button"
                        onClick={() => setLabelsRef(r.referencia)}
                        disabled={!r.referencia}
                        title="Imprimir etiquetas de bodega para esta recepción"
                        data-testid={`btn-labels-${i}`}
                        className="inline-flex items-center gap-1.5 bg-agro-green/10 hover:bg-agro-green/20 disabled:opacity-40 text-agro-green font-semibold text-[11px] rounded-md px-2.5 py-1.5 transition"
                      >
                        <BarcodeIcon size={13} weight="duotone" /> Etiquetas
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {receiving && <ReceiveModal po={receiving} onClose={() => setReceiving(null)} onSaved={() => { setReceiving(null); load(); }} />}
      {quickOpen && <QuickReceiveModal onClose={() => setQuickOpen(false)} onSaved={() => { setQuickOpen(false); load(); }} />}
      {labelsRef && <PrintLabelsModal referencia={labelsRef} onClose={() => setLabelsRef(null)} />}
    </Layout>
  );
}
